// =========================================================================
// HelpyDoc Metabolic · Motor de análisis nutricional por foto
//
// - buildMockResult(): genera un resultado realista a partir de
//   MOCK_FOOD_DATABASE para que la UI sea demoable HOY (data_mode = 'mock'),
//   sin depender del backend ni del ai-orchestrator.
// - safetyBiasFactor(): replica en el cliente la política de sesgo de
//   seguridad por objetivo (docs/02_app_nutricion/politica_estimacion_calorica.md) para que
//   el demo refleje el comportamiento real. En producción el sesgo lo aplica
//   el pipeline del backend; el frontend recibe `energyKcal` ya reportado.
// =========================================================================
import { MOCK_FOOD_DATABASE, type FoodItem, type UserGoal } from './DataService';
import type {
  AnalysisConfidence,
  AnalysisItem,
  ConfidenceLevel,
  NutritionAnalysisResult,
} from '@/models/analysis';

export const ANALYSIS_SCHEMA_VERSION = 'nutrition-analysis.v1';

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));
const round1 = (n: number) => Math.round(n * 10) / 10;

function levelFromScore(score: number): ConfidenceLevel {
  if (score >= 0.8) return 'high';
  if (score >= 0.5) return 'medium';
  return 'low';
}

/**
 * Factor de sesgo de seguridad aplicado a energía/carbohidratos/grasa
 * (la proteína NO se sesga). A mayor incertidumbre, mayor ajuste, con topes:
 *  - lose    → al alza   (hasta +30 %): no subestimar calorías es lo prioritario.
 *  - gain    → a la baja (hasta −25 %): no sobreestimar para favorecer el superávit.
 *  - maintain/sin objetivo → neutro (1.0).
 */
export function safetyBiasFactor(goal: UserGoal | undefined, score: number): number {
  const uncertainty = 1 - clamp(score, 0, 1);
  if (goal === 'lose') return 1 + 0.30 * uncertainty;
  if (goal === 'gain') return 1 - 0.25 * uncertainty;
  return 1;
}

function pickByCategory(category: FoodItem['category']): FoodItem | undefined {
  const pool = MOCK_FOOD_DATABASE.filter(f => f.category === category);
  if (pool.length === 0) return undefined;
  return pool[Math.floor(Math.random() * pool.length)];
}

/** Construye un AnalysisItem reportado (con sesgo) a partir de un FoodItem y una porción. */
function itemFromFood(food: FoodItem, goal: UserGoal | undefined): AnalysisItem {
  // Porción cercana a la típica con ±20 % de jitter (simula incertidumbre de tamaño).
  const jitter = 0.8 + Math.random() * 0.4;
  const grams = Math.round(food.typicalServingG * jitter);
  const f = grams / 100;

  // P50 honesto (insesgado).
  const kcalP50 = food.kcalPer100g * f;
  const proteinP50 = food.proteinPer100g * f;
  const carbsP50 = food.carbsPer100g * f;
  const fatP50 = food.fatPer100g * f;
  const fiberP50 = (food.fiberPer100g ?? 0) * f;

  // Confianza: alimentos verificados por nutricionista inspiran más confianza.
  const baseScore = food.verifiedByNutritionist ? 0.78 : 0.6;
  const score = clamp(round1(baseScore + (Math.random() * 0.16 - 0.08)), 0.35, 0.95);
  const confidence: AnalysisConfidence = { score, level: levelFromScore(score) };

  // Sesgo de seguridad (energía/carbos/grasa); proteína sin sesgar.
  const bias = safetyBiasFactor(goal, score);
  const energyKcal = Math.round(kcalP50 * bias);

  return {
    label: food.displayName,
    estimatedQuantity: { amount: grams, unit: 'g' },
    energyKcal,
    energyKcalRange: {
      low: Math.round(kcalP50 * 0.85),
      high: Math.round(kcalP50 * 1.2),
    },
    macros: {
      proteinG: round1(proteinP50),
      carbsG: round1(carbsP50 * bias),
      fatG: round1(fatP50 * bias),
      fiberG: round1(fiberP50),
    },
    confidence,
  };
}

/**
 * Genera un resultado de análisis verosímil para un plato compuesto.
 * Selecciona una proteína + un acompañamiento (cereal o plato combinado) y,
 * la mitad de las veces, un vegetal o una grasa saludable.
 */
export function buildMockResult(goal: UserGoal | undefined): NutritionAnalysisResult {
  const candidates: (FoodItem | undefined)[] = [
    pickByCategory('proteína') ?? pickByCategory('plato_combinado'),
    Math.random() < 0.5 ? pickByCategory('cereal') : pickByCategory('plato_combinado'),
    Math.random() < 0.5 ? pickByCategory('vegetal') : pickByCategory('grasa'),
  ];

  // Dedupe por id y descarta huecos.
  const seen = new Set<string>();
  const foods = candidates.filter((f): f is FoodItem => {
    if (!f || seen.has(f.id)) return false;
    seen.add(f.id);
    return true;
  });

  const items = foods.map(f => itemFromFood(f, goal));

  const totals = items.reduce(
    (acc, it) => ({
      energyKcal: acc.energyKcal + it.energyKcal,
      proteinG: round1(acc.proteinG + it.macros.proteinG),
      carbsG: round1(acc.carbsG + it.macros.carbsG),
      fatG: round1(acc.fatG + it.macros.fatG),
      fiberG: round1((acc.fiberG ?? 0) + (it.macros.fiberG ?? 0)),
    }),
    { energyKcal: 0, proteinG: 0, carbsG: 0, fatG: 0, fiberG: 0 },
  );

  const avgScore = items.length
    ? round1(items.reduce((s, it) => s + it.confidence.score, 0) / items.length)
    : 0.5;
  const overallConfidence: AnalysisConfidence = { score: avgScore, level: levelFromScore(avgScore) };

  const warnings: string[] = ['Porción estimada sin referencia de escala clara — ajusta los gramos si lo ves necesario.'];
  if (overallConfidence.level === 'low') {
    warnings.push('Confianza baja: la imagen no permite juzgar bien el tamaño de las porciones.');
  }
  if (goal === 'lose') {
    warnings.push('Estimación conservadora al alza acorde a tu objetivo de pérdida de peso.');
  }

  return {
    schemaVersion: ANALYSIS_SCHEMA_VERSION,
    items,
    totals,
    overallConfidence,
    warnings,
    notes: 'Estimación simulada en el dispositivo (modo demo). Se asume aceite de cocción y aliños no visibles.',
    goalApplied: goal,
  };
}
